import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import type { DashboardContextValue, WidgetLayout, DashboardLayoutConfig } from './types';

const DashboardContext = createContext<DashboardContextValue | null>(null);

interface DashboardProviderProps {
  children: React.ReactNode;
  /** Initial layout configuration */
  initialConfig: DashboardLayoutConfig;
  /** Callback to save layout (e.g., API call) */
  onSave?: (layout: WidgetLayout[]) => Promise<void>;
  /** Default layout to reset to */
  defaultLayout?: WidgetLayout[];
}

export const DashboardProvider: React.FC<DashboardProviderProps> = ({
  children,
  initialConfig,
  onSave,
  defaultLayout,
}) => {
  const [layout, setLayout] = useState<WidgetLayout[]>(initialConfig.layouts);
  const [originalLayout] = useState<WidgetLayout[]>(initialConfig.layouts);
  const [isEditing, setIsEditing] = useState(false);

  const isDirty = useMemo(() => {
    return JSON.stringify(layout) !== JSON.stringify(originalLayout);
  }, [layout, originalLayout]);

  const updateLayout = useCallback((newLayout: WidgetLayout[]) => {
    setLayout(newLayout);
  }, []);

  const addWidget = useCallback((widgetId: string, widgetType: string) => {
    // Find first available position
    const maxY = layout.reduce((max, item) => Math.max(max, item.y + item.h), 0);

    const newWidget: WidgetLayout = {
      i: widgetId,
      x: 0,
      y: maxY,
      w: 4,
      h: 4,
    };

    setLayout((prev) => [...prev, newWidget]);
  }, [layout]);

  const removeWidget = useCallback((widgetId: string) => {
    setLayout((prev) => prev.filter((item) => item.i !== widgetId));
  }, []);

  const saveLayout = useCallback(async () => {
    if (onSave) {
      await onSave(layout);
    }
  }, [layout, onSave]);

  const resetLayout = useCallback(() => {
    setLayout(defaultLayout || originalLayout);
  }, [defaultLayout, originalLayout]);

  const value = useMemo(
    () => ({
      layout,
      isEditing,
      setIsEditing,
      updateLayout,
      addWidget,
      removeWidget,
      saveLayout,
      resetLayout,
      isDirty,
    }),
    [layout, isEditing, updateLayout, addWidget, removeWidget, saveLayout, resetLayout, isDirty]
  );

  return <DashboardContext.Provider value={value}>{children}</DashboardContext.Provider>;
};

export const useDashboard = (): DashboardContextValue => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
};

export const useDashboardOptional = (): DashboardContextValue | null => {
  return useContext(DashboardContext);
};
